<template>
    <div class="padding-normal">
        <div class="normal-container">
            <h2>{{ title }}</h2>
            <div
                id="productSliderTabs"
                role="tablist"
                aria-label="Product categories"
                class="flex flex-row space-x-4"
                v-if="tabs && tabs.length > 1">
                <h3
                    role="tab"
                    :aria-selected="activeTab === index"
                    :aria-controls="`product-slider-panel-${index}`"
                    :id="`product-slider-tab-${index}`"
                    :tabindex="activeTab === index ? 0 : -1"
                    class="mb-2 mt-6 cursor-pointer text-[10px] transition-all hover:border-b-2 hover:border-green-500 lg:mb-8 lg:mt-6 lg:text-lg"
                    :class="{
                        'border-b-2 border-green-500 text-black': activeTab === index,
                        'text-gray-500': activeTab !== index,
                    }"
                    v-for="(tab, index) in tabs"
                    :key="index"
                    @click="handleClick(index)"
                    @keydown="handleKeyDown($event, index)"
                    @keydown.enter.prevent="handleClick(index)"
                    @keydown.space.prevent="handleClick(index)">
                    {{ (tab as any)?.title }}
                </h3>
            </div>
        </div>
        <div class="w-full overflow-hidden">
            <ProductSlide
                v-for="(tab, index) in tabs"
                :key="index"
                role="tabpanel"
                :id="`product-slider-panel-${index}`"
                :aria-labelledby="`product-slider-tab-${index}`"
                :aria-hidden="activeTab !== index"
                v-show="activeTab === index"
                :activeTab="tab"
                :tabIndex="index"
                :componentId="componentId"
                :showLabels="showLabels"
                :tabs="tabs"
                :title="getTabTitle(tab)"
                :desktop-productcard-size="desktopProductcardSize || ''" />
        </div>
    </div>
</template>

<script setup lang="ts">
    const props = defineProps({
        image: Object,
        title: String,
        tabs: Array,
        categories: Array,
        categoryId: String,
        product: Object,
        desktopProductcardSize: String,
        dataSource: String,
        // Sanity _key of this block, passed by the BlockResolver
        componentId: String,
        showLabels: {
            type: Boolean,
            default: true,
        },
    });

    const activeTab = ref(0);

    function handleKeyDown(event: KeyboardEvent, index: number) {
        const tabsLength = props.tabs?.length || 0;

        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                activeTab.value = index > 0 ? index - 1 : tabsLength - 1;
                break;
            case 'ArrowRight':
                event.preventDefault();
                activeTab.value = index < tabsLength - 1 ? index + 1 : 0;
                break;
            case 'Home':
                event.preventDefault();
                activeTab.value = 0;
                break;
            case 'End':
                event.preventDefault();
                activeTab.value = tabsLength - 1;
                break;
        }

        // Focus the newly active tab after keyboard navigation
        nextTick(() => {
            const tabElement = document.getElementById(`product-slider-tab-${activeTab.value}`);
            if (tabElement) {
                tabElement.focus();
            }
        });
    }

    function handleClick(index: number) {
        activeTab.value = index;
    }

    function getTabTitle(tab: any): string {
        const title = tab?.title;
        return typeof title === 'string' ? title : '';
    }
</script>
